import type { FunctionComponent, ReactNode } from 'react';
import classnames from 'classnames';
import { IconButton } from './Button';
import { icons } from './Icon';
import { BannerAttention, BannerSize, BannerStyle } from '../../../typings/enums';
import Flex from './Flex';

type BannerProps = {
  /**
   * Title for the banner.
   */
  title: string;
  /**
   * Body content for the banner, rendered as HTML.
   */
  body: string;
  /**
   * Determines the background color of the banner.
   */
  bannerStyle: BannerStyle;
  /**
   * If the banner is user dismissible
   */
  isDismissible?: boolean;
  /**
   * Dismiss handler, required if isDismissible is true.
   */
  onDismiss?: () => void;
  /**
   * Determines the size of the banner.
   */
  bannerSize?: BannerSize;
  /**
   * Determines the attention level of the banner.
   */
  bannerAttention?: BannerAttention;
  /**
   * Actions to be displayed next to the title and body.
   */
  actions?: ReactNode;
};

/**
 * Generic banner component.
 *
 */
const Banner: FunctionComponent<BannerProps> = ({
  title,
  body,
  bannerStyle,
  bannerSize,
  bannerAttention = BannerAttention.High,
  isDismissible,
  onDismiss,
  actions,
}) => (
  <div
    className={classnames('fossa-banner', {
      'fossa-banner--low': bannerStyle === BannerStyle.Low && bannerAttention === BannerAttention.High,
      'fossa-banner--medium': bannerStyle === BannerStyle.Medium && bannerAttention === BannerAttention.High,
      'fossa-banner--high': bannerStyle === BannerStyle.High && bannerAttention === BannerAttention.High,
      'fossa-banner--success': bannerStyle === BannerStyle.Success && bannerAttention === BannerAttention.High,
      'fossa-banner--info': bannerStyle === BannerStyle.Info && bannerAttention === BannerAttention.High,
      'fossa-banner--low--attention--low': bannerStyle === BannerStyle.Low && bannerAttention === BannerAttention.Low,
      'fossa-banner--medium--attention--low':
        bannerStyle === BannerStyle.Medium && bannerAttention === BannerAttention.Low,
      'fossa-banner--high--attention--low': bannerStyle === BannerStyle.High && bannerAttention === BannerAttention.Low,
      'fossa-banner--success--attention--low':
        bannerStyle === BannerStyle.Success && bannerAttention === BannerAttention.Low,
      'fossa-banner--info--attention--low':
        bannerStyle === BannerStyle.Info && bannerAttention === BannerAttention.Low,
      'fossa-banner__size--small': bannerSize === BannerSize.Small,
      'fossa-banner__size--medium': bannerSize === BannerSize.Medium,
    })}
  >
    <Flex direction="row" align="flex-start" justify="space-between" gap="8px">
      <Flex direction="column" align="flex-start">
        <div className="fossa-banner__title">
          <div className="fossa-banner__title__text">{title}</div>
        </div>
        {!!body && (
          // eslint-disable-next-line react/no-danger -- We sanitize this before we store it on the backend, so it *should* be safe, however we should try to remove this if possible.
          <div className="fossa-banner__body" dangerouslySetInnerHTML={{ __html: body }} />
        )}
      </Flex>
      {actions && <div className="fossa-banner__actions">{actions}</div>}
      {isDismissible && (
        <div className="fossa-banner__title__dismiss">
          <IconButton
            id="dismiss-banner"
            icon={icons.times}
            onClick={() => {
              if (onDismiss) {
                onDismiss();
              }
            }}
          />
        </div>
      )}
    </Flex>
  </div>
);

export default Banner;
